import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { EventOverview } from 'src/app/core-module/models/Events/event-overview';
import { EventWorkflow } from 'src/app/core-module/models/Events/event-workflow';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { EventsService } from 'src/app/core-module/services/events.service';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { EventOverviewPanelComponent } from "./event-overview-panel/event-overview-panel.component";

import { FormsModule } from '@angular/forms';
import { EventStatusItem } from 'src/app/core-module/models/Events/event-status-item';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { GetEventsFilterRequest } from 'src/app/core-module/models/Events/get-events-filter-request';
import { SelectModule } from 'primeng/select';
import { EventTemplate } from 'src/app/core-module/models/Events/event-template';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-events',
  standalone: true,
  imports: [FormsModule, EventOverviewPanelComponent, MultiSelectModule, DatePickerModule, SelectModule, ConfirmPopupModule],
  providers: [ConfirmationService],
  templateUrl: './events.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './events.component.scss'
})
export class EventsComponent {

  public startingEvent: boolean = false;
  public workflow: EventWorkflow;
  public availableTemplates: EventTemplate[] = [];
  public selectedTemplateId: number | null = null;

  public originalEvents: EventOverview[] = [];
  public filteredEvents: EventOverview[] = [];
  public eventStatuses: EventStatusItem[] = [];
  public locationOptions: { id: number, name: string }[] = [];

  public selectedStatusIds: number[] = [];
  public selectedLocationIds: number[] = [];
  public reportedFrom: Date | null = null;
  public reportedTo: Date | null = null;
  private lastFilter: GetEventsFilterRequest = new GetEventsFilterRequest();
  public loadingEvents: boolean = false;
  private templateConfirmTarget: EventTarget | null = null;

  constructor(public Events: EventsService, public router: Router, private toast: ToastrService, private confirmationService: ConfirmationService){}

  ngOnInit(){
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    this.reportedTo = today;
    this.reportedFrom = oneYearAgo;
    this.applyFilters();
    this.loadStatuses();
  }

  loadStatuses(){
    this.Events.getEventStatuses().subscribe({
      next: (statuses: EventStatusItem[]) => {
        this.eventStatuses = statuses || [];
        this.applyDefaultStatuses();
        this.applyFilters();
      },
      error: () => {
        this.eventStatuses = [];
      }
    });
  }

  private applyDefaultStatuses(){
    if (this.selectedStatusIds.length === 0 && this.eventStatuses.length > 0){
        this.selectedStatusIds = this.eventStatuses
          .filter(s => (s.EventStatusDescription || '').toLowerCase().includes('open'))
          .map(s => s.EventStatusID);
    }
  }

  private buildLocationOptions(events: EventOverview[]): {id:number, name:string}[]{
    const dedup: Record<number, string> = {};
    (events || []).forEach(e => {
      if (e.CustomerLocationID && !dedup[e.CustomerLocationID]){
        dedup[e.CustomerLocationID] = e.LocationName || `Location ${e.CustomerLocationID}`;
      }
    });
    return Object.keys(dedup).map(k => ({ id: Number(k), name: dedup[Number(k)] }));
  }

  applyFilters(){
    const filter = new GetEventsFilterRequest();
    filter.StatusIds = [...this.selectedStatusIds];
    filter.LocationIds = [...this.selectedLocationIds];
    filter.FromDate = this.reportedFrom ? this.stripTime(this.reportedFrom).toISOString() : undefined;
    filter.ToDate = this.reportedTo ? this.stripTime(this.reportedTo).toISOString() : undefined;
    this.lastFilter = filter;
    this.loadingEvents = true;
    this.Events.getTopLevelEvents(filter).pipe(
      tap(res => {
        this.loadingEvents = false;
        this.filteredEvents = res;
        this.locationOptions = this.buildLocationOptions(res);
      })
    ).subscribe({
      error: () => {
        this.loadingEvents = false;
      }
    });
  }

  clearFilters(){
    this.selectedLocationIds = [];
    this.reportedFrom = null;
    this.reportedTo = null;
    this.applyDefaultStatuses();
    this.applyFilters();
  }

  private stripTime(d: Date): Date{
    const c = new Date(d);
    c.setHours(0,0,0,0);
    return c;
  }

  addEvent(event?: Event){
    this.templateConfirmTarget = event?.currentTarget as EventTarget ?? event?.target as EventTarget ?? null;
    this.startingEvent = true;
    this.Events.getAvailableEventTemplates().pipe(
      tap(res => {
        const templates = (res?.Templates ?? []).map(t => ({
          ...t,
          TemplateName: t.TemplateName || t.WorkflowName || `Template ${t.EventWorkflowID}`
        }));
        if (templates.length === 0){
          this.toast.addToastr(new Toastr({Type: 3, Message:'No event templates are available for your locations.'}));
          this.startingEvent = false;
          return;
        }
        if (templates.length === 1){
          this.startEventWithTemplate(templates[0]);
          return;
        }
        this.availableTemplates = templates;
        this.selectedTemplateId = null;
        this.startingEvent = false;
        this.confirmationService.confirm({
          key: 'templateSelect',
          target: this.templateConfirmTarget ?? undefined,
          defaultFocus: 'accept',
          accept: () => this.confirmTemplateSelection(),
          reject: () => this.cancelTemplateSelection()
        });
      })
    ).subscribe({
      error: () => {
        this.toast.addToastr(new Toastr({Type: 3, Message:'An error occurred getting event templates.'}));
        this.startingEvent = false;
      }
    });
  }

  confirmTemplateSelection(){
    const chosen = this.availableTemplates.find(t => t.EventWorkflowID === this.selectedTemplateId);
    if (chosen){
      this.startEventWithTemplate(chosen);
    } else {
      this.toast.addToastr(new Toastr({Type: 2, Message:'Please select a template.'}));
    }
  }

  cancelTemplateSelection(){
    this.confirmationService.close();
    this.selectedTemplateId = null;
  }

  private startEventWithTemplate(template: EventTemplate){
    const auditPayload = (template as any).MainAudit;
    if (!auditPayload){
      this.toast.addToastr(new Toastr({Type: 3, Message:'Template is missing its audit.'}));
      this.startingEvent = false;
      return;
    }
    this.workflow = template as unknown as EventWorkflow;
    this.selectedTemplateId = null;
    this.startingEvent = false;
    this.confirmationService.close();
    this.router.navigateByUrl('/selectlocation', { state: { "audit": auditPayload, "isEvent": true, "EventWorkflowID": this.workflow.EventWorkflowID } });
  }
}
